import { useEffect, useId, useRef, useState } from 'react';
import { Button, Icon, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHICH TASK — A SEARCH, NOT A DROPDOWN. Feedback round 4 item 21.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The Időrögzítés quick-log has searched for its task since C11. The calendar's entry dialog used a
 * `Select` over whatever `GET /tasks` happened to return — **fifty rows, the API's default page**,
 * in id order, with no way to look for the fifty-first. On any workspace past its first month the
 * task somebody wanted was simply not in the list, and nothing on screen said so.
 *
 * So the quick-log's control is extracted rather than copied: one search box, one result list, one
 * set of keyboard and blur rules, in both places.
 *
 * ⚠️ **Presentational — it is handed `results` and knows nothing about the session.** Every screen
 * in this app renders props and the route fetches; `task-picker.tsx` records the same rule for the
 * relation picker, and `projects-screen.tsx` records what happened the one time a screen reached
 * for router context instead.
 */
export interface TaskChoice {
  readonly id: string;
  readonly key: string;
  readonly title: string;
  readonly projectName: string | null;
  readonly projectColor: string | null;
}

export function TaskSearchField({
  pick,
  results,
  onQuery,
  onPick,
  onClear,
  swatch,
}: {
  /** The chosen task, or `null` while searching. */
  readonly pick: TaskChoice | null;
  readonly results: readonly TaskChoice[];
  /** Called on every keystroke, including the empty string when the box is cleared. */
  readonly onQuery: (query: string) => void;
  readonly onPick: (task: TaskChoice) => void;
  readonly onClear: () => void;
  /** The project dot, which each screen draws its own way. */
  readonly swatch: (color: string | null) => React.ReactNode;
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  /**
   * 27 Oct 2026 · ↑ and ↓ move through the results, Enter takes the highlighted one, Escape closes
   * the list. ⚠️ **Focus never leaves the search box** — the highlight is `aria-activedescendant` —
   * so typing after an arrow simply carries on searching, which is what the product owner asked
   * for. `-1` is nothing highlighted; a new search starts there again.
   */
  const [active, setActive] = useState(-1);
  const listId = useId();
  const list = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    list.current?.querySelector(`[data-index="${String(active)}"]`)?.scrollIntoView({
      block: 'nearest',
    });
  }, [active]);

  const choose = (choice: TaskChoice): void => {
    onPick(choice);
    setQuery('');
    setOpen(false);
  };

  if (pick !== null) {
    return (
      <div className="bg-muted/40 flex h-9 items-center gap-2 rounded-md border px-2.5">
        {swatch(pick.projectColor)}
        <span className="text-muted-foreground shrink-0 text-[11.5px]">{pick.key}</span>
        <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium">{pick.title}</span>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => {
            setQuery('');
            onQuery('');
            onClear();
          }}
          aria-label={t('time.log.otherTask')}
        >
          <Icon name="x" size={13} />
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <TextInput
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(-1);
          setOpen(true);
          onQuery(event.target.value);
        }}
        onFocus={() => {
          setOpen(true);
        }}
        /* ⚠️ `onBlur` closes the list, so the result buttons fire on `mousedown` — a click's `blur`
           lands first and would unmount the button mid-click. */
        onBlur={() => {
          setOpen(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            if (results.length === 0) return;
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActive((at) =>
              at === -1 && step < 0
                ? results.length - 1
                : Math.min(results.length - 1, Math.max(0, at + step)),
            );
            return;
          }
          if (event.key === 'Enter') {
            const choice = open ? results[active] : undefined;
            if (choice === undefined) return;
            /* ⚠️ Not the dialog's submit — Enter here means *this task*. */
            event.preventDefault();
            choose(choice);
            return;
          }
          if (event.key === 'Escape' && open) {
            event.stopPropagation();
            setOpen(false);
          }
        }}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        {...(open && active >= 0 ? { 'aria-activedescendant': `${listId}-${String(active)}` } : {})}
        placeholder={t('time.log.search')}
        aria-label={t('time.log.search')}
        className="h-9 w-full"
      />
      {!open ? null : (
        <div
          ref={list}
          id={listId}
          role="listbox"
          className="bg-popover absolute top-full right-0 left-0 z-20 mt-1 max-h-[240px] overflow-y-auto rounded-lg border p-1 shadow-lg"
        >
          {results.map((choice, index) => (
            <button
              key={choice.id}
              id={`${listId}-${String(index)}`}
              data-index={index}
              type="button"
              role="option"
              aria-selected={index === active}
              tabIndex={-1}
              onMouseDown={() => {
                choose(choice);
              }}
              onMouseEnter={() => {
                setActive(index);
              }}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left ${
                index === active ? 'bg-accent' : ''
              }`}
            >
              {swatch(choice.projectColor)}
              <span className="text-muted-foreground w-[64px] shrink-0 truncate text-[11.5px] tabular-nums">
                {choice.key}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12.5px]">{choice.title}</span>
              <span className="text-muted-foreground shrink-0 text-[11px]">
                {choice.projectName ?? t('time.noProject')}
              </span>
            </button>
          ))}
          {results.length === 0 && (
            <p className="text-muted-foreground px-2 py-6 text-center text-[12px]">
              {t('time.log.noResults')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
